/*
 * «Font» per Discord e Instagram: in realtà sono caratteri Unicode speciali (𝓐 𝔄 𝔸 Ⓐ 🅰 ᴀʙᴄ…),
 * gli stessi dei generatori di font per Instagram. Su Discord si vedono ovunque, anche da telefono.
 * Menzioni, canali, emoji del server, link e variabili come {user} non vengono toccati.
 */
const A = 65, a = 97;
const cp = (n) => String.fromCodePoint(n);

const math = (up, low, digit, holes = {}) => (ch) => {
  if (holes[ch]) return holes[ch];
  if (ch >= "A" && ch <= "Z") return cp(up + ch.charCodeAt(0) - A);
  if (ch >= "a" && ch <= "z" && low) return cp(low + ch.charCodeAt(0) - a);
  if (ch >= "0" && ch <= "9" && digit) return cp(digit + Number(ch));
  return ch;
};
const table = (from, to) => {
  const f = [...from], t = [...to];
  const m = Object.fromEntries(f.map((c, i) => [c, t[i]]));
  return (ch) => m[ch] ?? ch;
};
const combining = (mark) => (ch) => (/\s/.test(ch) ? ch : ch + mark);

const SCRIPT = { B: "ℬ", E: "ℰ", F: "ℱ", H: "ℋ", I: "ℐ", L: "ℒ", M: "ℳ", R: "ℛ", e: "ℯ", g: "ℊ", o: "ℴ" };
const FRAKTUR = { C: "ℭ", H: "ℌ", I: "ℑ", R: "ℜ", Z: "ℨ" };
const DOUBLE = { C: "ℂ", H: "ℍ", N: "ℕ", P: "ℙ", Q: "ℚ", R: "ℝ", Z: "ℤ" };
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

export const FANCY_STYLES = [
  { key: "boldScript", name: "Corsivo grassetto", map: math(0x1d4d0, 0x1d4ea, 0x1d7ce) },
  { key: "script", name: "Corsivo elegante", map: math(0x1d49c, 0x1d4b6, null, SCRIPT) },
  { key: "boldFraktur", name: "Gotico grassetto", map: math(0x1d56c, 0x1d586, 0x1d7ce) },
  { key: "fraktur", name: "Gotico", map: math(0x1d504, 0x1d51e, null, FRAKTUR) },
  { key: "doubleStruck", name: "Doppio contorno", map: math(0x1d538, 0x1d552, 0x1d7d8, DOUBLE) },
  { key: "bold", name: "Grassetto con grazie", map: math(0x1d400, 0x1d41a, 0x1d7ce) },
  { key: "italic", name: "Corsivo con grazie", map: math(0x1d434, 0x1d44e, null, { h: "ℎ" }) },
  { key: "boldItalic", name: "Grassetto corsivo", map: math(0x1d468, 0x1d482, 0x1d7ce) },
  { key: "sans", name: "Senza grazie", map: math(0x1d5a0, 0x1d5ba, 0x1d7e2) },
  { key: "sansBold", name: "Senza grazie grassetto", map: math(0x1d5d4, 0x1d5ee, 0x1d7ec) },
  { key: "sansItalic", name: "Senza grazie corsivo", map: math(0x1d608, 0x1d622) },
  { key: "sansBoldItalic", name: "Senza grazie grassetto corsivo", map: math(0x1d63c, 0x1d656) },
  { key: "mono", name: "Macchina da scrivere", map: math(0x1d670, 0x1d68a, 0x1d7f6) },
  { key: "circled", name: "Nei cerchi", map: table(LETTERS, "ⒶⒷⒸⒹⒺⒻⒼⒽⒾⒿⓀⓁⓂⓃⓄⓅⓆⓇⓈⓉⓊⓋⓌⓍⓎⓏⓐⓑⓒⓓⓔⓕⓖⓗⓘⓙⓚⓛⓜⓝⓞⓟⓠⓡⓢⓣⓤⓥⓦⓧⓨⓩ⓪①②③④⑤⑥⑦⑧⑨") },
  { key: "negCircled", name: "Cerchi pieni", map: (ch) => (/[A-Za-z]/.test(ch) ? cp(0x1f150 + ch.toUpperCase().charCodeAt(0) - A) : table("0123456789", "⓿❶❷❸❹❺❻❼❽❾")(ch)) },
  { key: "squared", name: "Nei quadrati", map: (ch) => (/[A-Za-z]/.test(ch) ? cp(0x1f130 + ch.toUpperCase().charCodeAt(0) - A) : ch) },
  { key: "negSquared", name: "Quadrati pieni", map: (ch) => (/[A-Za-z]/.test(ch) ? cp(0x1f170 + ch.toUpperCase().charCodeAt(0) - A) : ch) },
  { key: "fullwidth", name: "Largo", map: (ch) => (/[A-Za-z0-9!?.,:;]/.test(ch) ? cp(ch.charCodeAt(0) + 0xfee0) : ch === " " ? "　" : ch) },
  { key: "smallCaps", name: "Maiuscoletto", map: table("abcdefghijklmnopqrstuvwxyz", "ᴀʙᴄᴅᴇꜰɢʜɪᴊᴋʟᴍɴᴏᴘǫʀꜱᴛᴜᴠᴡxʏᴢ") },
  { key: "superscript", name: "Piccolo in alto", map: table("abcdefghijklmnoprstuvwxyzABDEGHIJKLMNOPRTUVW0123456789", "ᵃᵇᶜᵈᵉᶠᵍʰⁱʲᵏˡᵐⁿᵒᵖʳˢᵗᵘᵛʷˣʸᶻᴬᴮᴰᴱᴳᴴᴵᴶᴷᴸᴹᴺᴼᴾᴿᵀᵁⱽᵂ⁰¹²³⁴⁵⁶⁷⁸⁹") },
  { key: "parenthesized", name: "Tra parentesi", map: (ch) => (/[a-z]/i.test(ch) ? cp(0x249c + ch.toLowerCase().charCodeAt(0) - a) : /[1-9]/.test(ch) ? cp(0x2474 + Number(ch) - 1) : ch) },
  { key: "strike", name: "Barrato", map: combining("\u0336") },
  { key: "underline", name: "Sottolineato", map: combining("\u0332") },
  { key: "upsideDown", name: "Capovolto", reverse: true, map: table("abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,!?'\"()[]{}<>&_", "ɐqɔpǝɟƃɥᴉɾʞlɯuodbɹsʇnʌʍxʎz∀ᗺƆᗡƎℲ⅁HIſꓘ˥WNOԀΌᴚS⊥∩ΛMX⅄Z0ƖᄅƐㄣϛ9ㄥ86˙'¡¿,„)(][}{><⅋‾") },
];

// Pezzi da non trasformare: menzioni, canali, ruoli, emoji personalizzate, timestamp, link e variabili {x}.
const PROTECT = /(<[@#][&!]?\d+>|<a?:\w+:\d+>|<t:\d+(?::\w)?>|https?:\/\/\S+|\{[\w.]+\}|@everyone|@here)/g;

export function toFancy(text, key) {
  const style = FANCY_STYLES.find((s) => s.key === key);
  if (!style) return text;
  const out = String(text).split(PROTECT).map((part, i) => (i % 2 ? part : [...toPlain(part)].map(style.map).join("")));
  return style.reverse ? out.reverse().map((p, i) => (i % 2 ? p : [...p].reverse().join(""))).join("") : out.join("");
}

// ── ritorno al testo normale ──
let plainMap = null;
export function toPlain(text) {
  if (!plainMap) {
    plainMap = new Map();
    for (const s of FANCY_STYLES) {
      if (s.reverse || s.key === "strike" || s.key === "underline") continue;
      for (const ch of LETTERS) {
        const f = s.map(ch);
        if (f !== ch && !plainMap.has(f)) plainMap.set(f, ch);
      }
    }
  }
  return [...String(text).replace(/[\u0336\u0332]/g, "")]
    .map((c) => plainMap.get(c) ?? (c === "　" ? " " : c >= "！" && c <= "～" ? cp(c.codePointAt(0) - 0xfee0) : c))
    .join("");
}
